import React, { useEffect, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Divider,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Input,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { FiPhone, FiTrash } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { provisionCampaignNumber, releaseCampaignNumber } from '../../services/phoneService';

const inputProps = {
  bg: '#171717',
  borderColor: 'rgba(255,255,255,0.12)',
  color: '#fafafa',
  fontSize: 'sm',
  _placeholder: { color: 'rgba(255,255,255,0.25)' },
  _hover: { borderColor: 'rgba(255,255,255,0.2)' },
  _focus: { borderColor: 'rgba(255,255,255,0.35)', boxShadow: 'none' },
};

const primaryBtn = {
  bg: '#fafafa',
  color: '#0a0a0a',
  borderRadius: 'full',
  fontWeight: '600',
  fontSize: '13px',
  h: '38px',
  _hover: { opacity: 0.85 },
};

const dangerBtn = {
  bg: 'transparent',
  borderRadius: 'full',
  fontWeight: '500',
  fontSize: '13px',
  h: '36px',
  border: '1px solid rgba(239,68,68,0.3)',
  color: 'rgba(239,68,68,0.7)',
  _hover: {
    borderColor: 'rgba(239,68,68,0.5)',
    color: 'rgba(239,68,68,0.9)',
    bg: 'rgba(239,68,68,0.06)',
  },
};

const ghostBtn = {
  bg: 'transparent',
  borderRadius: 'full',
  fontWeight: '500',
  fontSize: '13px',
  h: '36px',
  border: '1px solid rgba(255,255,255,0.15)',
  color: 'rgba(255,255,255,0.6)',
  _hover: {
    borderColor: 'rgba(255,255,255,0.3)',
    color: 'rgba(255,255,255,0.9)',
    bg: 'rgba(255,255,255,0.04)',
  },
};

const OutboundPhoneSection = ({ parentPaidOk }) => {
  const toast = useToast();
  const { user, fetchUserProfile } = useAuth();
  const [areaCode, setAreaCode] = useState('');
  const [numberNickname, setNumberNickname] = useState('');
  const [loading, setLoading] = useState(false);
  const [showReleaseConfirm, setShowReleaseConfirm] = useState(false);
  const cancelReleaseRef = useRef();

  useEffect(() => {
    if (user && !user.retell_phone_number) {
      setNumberNickname(user.first_name ? `${user.first_name}'s Burner` : '');
    }
  }, [user]);

  if (!parentPaidOk || !user) {
    return null;
  }

  const hasNumber = Boolean(user.retell_phone_number);

  const handleProvision = async () => {
    setLoading(true);
    try {
      await provisionCampaignNumber({ areaCode: areaCode || undefined });
      await fetchUserProfile();
      setAreaCode('');
      toast({
        title: 'Number provisioned',
        description: 'Your campaign phone number is ready.',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to provision number. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRelease = async () => {
    setLoading(true);
    try {
      await releaseCampaignNumber();
      await fetchUserProfile();
      setShowReleaseConfirm(false);
      toast({
        title: 'Number released',
        description: 'Your campaign number has been released.',
        status: 'info',
        duration: 4000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to release number. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box id="outbound-phone" scrollMarginTop="6rem">
      <Box
        p={6}
        borderRadius="12px"
        border="1px solid rgba(255,255,255,0.10)"
        bg="#0f0f0f"
      >
        <VStack spacing={5} align="stretch">
          <HStack justify="space-between" align="center">
            <VStack align="start" spacing={0.5}>
              <Text fontSize="sm" fontWeight="600" color="#fafafa" letterSpacing="-0.01em">
                Campaign Phone Line
              </Text>
              <Text fontSize="xs" color="rgba(255,255,255,0.3)">
                A dedicated US number for outbound call campaigns
              </Text>
            </VStack>
            {hasNumber && (
              <Box
                px={1.5}
                py="2px"
                borderRadius="full"
                bg="rgba(74,222,128,0.08)"
                border="1px solid rgba(74,222,128,0.2)"
                flexShrink={0}
              >
                <Text
                  fontSize="10px"
                  fontWeight="600"
                  color="rgba(74,222,128,0.7)"
                  letterSpacing="0.06em"
                  textTransform="uppercase"
                >
                  Active
                </Text>
              </Box>
            )}
          </HStack>

          <Divider borderColor="rgba(255,255,255,0.08)" />

          {!hasNumber ? (
            <VStack spacing={5} align="stretch">
              <Text fontSize="sm" color="#a3a3a3" lineHeight="1.7">
                Get a dedicated US phone number for your outbound call campaigns. Your personal number
                stays completely private.
              </Text>

              <VStack spacing={2.5} align="stretch">
                <FormControl>
                  <FormLabel fontSize="sm" color="#a3a3a3" mb={1.5}>
                    Nickname
                  </FormLabel>
                  <Input
                    value={numberNickname}
                    onChange={(e) => setNumberNickname(e.target.value.slice(0, 40))}
                    placeholder="e.g. Sayan's Burner"
                    {...inputProps}
                  />
                </FormControl>
                <HStack spacing={3}>
                  <Input
                    value={areaCode}
                    onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, '').slice(0, 3))}
                    placeholder="Area code"
                    maxLength={3}
                    textAlign="center"
                    letterSpacing="0.1em"
                    w="110px"
                    flexShrink={0}
                    {...inputProps}
                  />
                  <Button
                    {...primaryBtn}
                    leftIcon={<Icon as={FiPhone} boxSize={3.5} />}
                    onClick={handleProvision}
                    isLoading={loading}
                    loadingText="Provisioning..."
                    flex={1}
                  >
                    Get Campaign Number
                  </Button>
                </HStack>
                <Text fontSize="xs" color="rgba(255,255,255,0.3)" letterSpacing="0.02em">
                  $7 / month &nbsp;·&nbsp; US &nbsp;·&nbsp; Area code optional — leave blank for any
                  available number
                </Text>
              </VStack>
            </VStack>
          ) : (
            <VStack spacing={4} align="stretch">
              <HStack justify="space-between" align="center" flexWrap="wrap" gap={3}>
                <VStack align="start" spacing={0.5}>
                  {user.retell_phone_number_nickname && (
                    <Text
                      fontSize="xs"
                      fontWeight="500"
                      color="rgba(255,255,255,0.45)"
                      letterSpacing="0.01em"
                    >
                      {user.retell_phone_number_nickname}
                    </Text>
                  )}
                  <Text
                    fontSize="2xl"
                    fontWeight="700"
                    color="#fafafa"
                    letterSpacing="-0.03em"
                    fontFamily="mono"
                  >
                    {user.retell_phone_number_pretty || user.retell_phone_number}
                  </Text>
                  {user.retell_phone_number_created_at && (
                    <Text fontSize="xs" color="rgba(255,255,255,0.3)">
                      Provisioned{' '}
                      {new Date(user.retell_phone_number_created_at).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </Text>
                  )}
                </VStack>
                <Button
                  {...dangerBtn}
                  leftIcon={<Icon as={FiTrash} boxSize={3.5} />}
                  onClick={() => setShowReleaseConfirm(true)}
                  isDisabled={loading}
                  flexShrink={0}
                >
                  Release
                </Button>
              </HStack>

              <Box
                p={3}
                borderRadius="8px"
                bg="rgba(255,255,255,0.02)"
                border="1px solid rgba(255,255,255,0.06)"
              >
                <Text fontSize="xs" color="rgba(255,255,255,0.3)" lineHeight="1.7">
                  This number is used for outbound call campaigns. Releasing it will immediately stop
                  all active campaigns that use this number.
                </Text>
              </Box>
            </VStack>
          )}
        </VStack>
      </Box>

      <AlertDialog
        isOpen={showReleaseConfirm}
        leastDestructiveRef={cancelReleaseRef}
        onClose={() => setShowReleaseConfirm(false)}
        isCentered
      >
        <AlertDialogOverlay bg="blackAlpha.700" />
        <AlertDialogContent
          bg="#0a0a0a"
          color="#fafafa"
          border="1px solid rgba(255,255,255,0.10)"
          borderRadius="12px"
        >
          <AlertDialogHeader fontSize="md" fontWeight="700" letterSpacing="-0.01em" pb={2}>
            Release Campaign Number
          </AlertDialogHeader>
          <AlertDialogBody>
            <Text fontSize="sm" color="#a3a3a3" lineHeight="1.7">
              Release{' '}
              <strong style={{ color: '#fafafa' }}>
                {user.retell_phone_number_pretty || user.retell_phone_number}
              </strong>
              ? Any active campaigns using this number will stop working immediately. This cannot be
              undone.
            </Text>
          </AlertDialogBody>
          <AlertDialogFooter gap={2} pt={2}>
            <Button
              ref={cancelReleaseRef}
              {...ghostBtn}
              onClick={() => setShowReleaseConfirm(false)}
              isDisabled={loading}
            >
              Cancel
            </Button>
            <Button {...dangerBtn} onClick={handleRelease} isLoading={loading} loadingText="Releasing...">
              Release Number
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Box>
  );
};

export default OutboundPhoneSection;
